import { Link } from 'gatsby';
import { XLogo, YoutubeLogo, TwitchLogo, InstagramLogo } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useServerStats } from '@/features/server-stats/hooks/useServerStats';
import { MAX_SERVER_CAPACITY, STORE_URL, SOCIAL_LINKS } from '@/config/constants';

const socials = [
  { href: SOCIAL_LINKS.twitter, label: 'X', Icon: XLogo },
  { href: SOCIAL_LINKS.youtube, label: 'YouTube', Icon: YoutubeLogo },
  { href: SOCIAL_LINKS.twitch, label: 'Twitch', Icon: TwitchLogo },
  { href: SOCIAL_LINKS.instagram, label: 'Instagram', Icon: InstagramLogo },
];

const Footer = () => {
  const { t } = useTranslation('common');
  const { playerCount, isOnline, isLoading } = useServerStats();
  const percentage = Math.min((playerCount / MAX_SERVER_CAPACITY) * 100, 100);

  return (
    <footer className="border-t border-border-muted bg-background pb-10 pt-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1.2fr]">
          <div>
            <div className="mb-6 flex items-center gap-3">
              <img src="/images/logo.webp" alt="" className="h-10 w-10 rounded-lg object-cover" />
              <div className="flex flex-col leading-none">
                <span className="text-lg font-bold tracking-tight text-foreground">ZestianMC</span>
                <span className="text-xs font-medium text-foreground-subtle">mc.zestian.co</span>
              </div>
            </div>
            <p className="mb-6 max-w-xs text-sm leading-relaxed text-foreground-muted">{t('footer.description')}</p>
            <div className="flex gap-2">
              {socials.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="rounded-lg bg-surface p-2 text-foreground-muted transition-colors hover:bg-surface-elevated hover:text-foreground"
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-foreground-subtle">{t('footer.navigation')}</h4>
            <ul className="space-y-3 text-sm text-foreground-muted">
              <li><Link to="/" className="transition-colors hover:text-foreground">{t('footer.home')}</Link></li>
              <li><Link to="/#modes" className="transition-colors hover:text-foreground">{t('footer.modes')}</Link></li>
              <li><Link to="/reglas/" className="transition-colors hover:text-foreground">{t('navbar.rules')}</Link></li>
              <li><Link to="/guias/" className="transition-colors hover:text-foreground">{t('navbar.guides')}</Link></li>
              <li><a href={STORE_URL} className="transition-colors hover:text-foreground">{t('footer.officialStore')}</a></li>
              <li><Link to="/privacidad/" className="transition-colors hover:text-foreground">{t('footer.privacy')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-foreground-subtle">{t('footer.serverStatus')}</h4>
            <div className="rounded-xl bg-surface p-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm text-foreground-muted">{t('footer.status')}</span>
                <span className={`text-xs font-semibold ${isOnline ? 'text-success' : 'text-error'}`}>
                  {isOnline ? t('footer.online') : t('footer.offline')}
                </span>
              </div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm text-foreground-muted">{t('footer.players')}</span>
                {isLoading ? (
                  <span className="h-5 w-16 animate-pulse rounded bg-surface-elevated" />
                ) : (
                  <span className="font-semibold tabular-nums text-foreground">
                    {playerCount.toLocaleString()} <span className="text-xs text-foreground-subtle">/ {MAX_SERVER_CAPACITY}</span>
                  </span>
                )}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-elevated">
                <div className="h-full bg-primary transition-all duration-1000" style={{ width: `${percentage}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-border-muted pt-8 md:flex-row">
          <p className="text-sm text-foreground-subtle">{t('footer.copyright')}</p>
          <p className="text-xs text-foreground-subtle">{t('footer.notAffiliated')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
